import { Controller } from "@hotwired/stimulus";
import { announceSessionChange } from "../session_events";

// Mortgage pre-approval walkthrough (SUR-2302 / SSD-22 "Walkthrough Views").
// The full questionnaire is ported from the ezhomesearch webapp's lender
// pre-approval funnel — same questions and answer options — adapted to
// Connect's stack:
//
//   answers accumulate client-side
//   → contact step requests a verification code (POST /_register — the
//     existing passwordless registration flow; both identifiers are captured,
//     and the code is sent by email)
//   → code step verifies (POST /auth/connect/code — resolves/creates the
//     Person and signs the visitor in)
//   → ONE final POST /_lead with the questionnaire folded into `message`,
//     landing a Note + connect_inquiry notification for the agent/LO.
//
// Verification is best-effort: "Submit without verifying" and any /_register
// failure fall through to the plain lead post, so a lost code never loses the
// lead.
const ANSWER_LABELS = {
  property_type: "Property type",
  property_usage: "Intended use",
  timeline: "Where in the process",
  working_with_agent: "Working with an agent",
  current_homeownership: "Current housing",
  first_time_buyer: "First-time buyer",
  selling_current_home: "Selling current home first",
  military_service: "US military service",
  budget: "Price range",
  employment_status: "Employment",
  annual_income: "Household income",
  credit_score: "Credit score",
  bankruptcy_foreclosure: "Bankruptcy/foreclosure (3 yrs)",
};

export default class extends Controller {
  static targets = [
    "step", "back", "next", "error", "success", "form",
    "email", "phone", "code", "codeHint",
    "progressLabel", "progressTrack", "progressFill",
  ];

  connect() {
    this.index = 0;
    this.answers = {};
    this.verified = false;
    this.busy = false;
    this.showStep();
  }

  // The footer's primary button is the walkthrough's ONLY primary action — the
  // design pins Back | Finish to the card's bottom edge rather than letting a
  // step grow its own submit — so this dispatches on what the current step is.
  next() {
    const current = this.stepTargets[this.index];
    if (current.hasAttribute("data-verify-step")) return this.verifyAndSubmit();
    if (current.querySelector("form")) return this.requestCode();

    // Require a selection on the choice steps before advancing.
    const group = current.dataset.choiceGroup;
    if (group && !this.answers[group]) {
      this.showError("Pick an option to continue.");
      return;
    }
    this.clearError();
    if (this.index < this.stepTargets.length - 1) {
      this.index += 1;
      this.showStep();
    }
  }

  // Freeze every way out of a step while it posts. Both footer buttons, because
  // the footer is the only way forward AND back: leaving Back live let a
  // visitor step away mid-request and then get yanked to the verify step (or
  // have their lead filed) when it landed, from a step they had left.
  //
  // Controller state as well as button state, because the verify step also
  // carries "Resend code" and "Submit without verifying" links, which POST but
  // can't be disabled the way a button can. Every posting path checks this
  // flag, so a frozen footer can't be walked around by clicking a link beside
  // it.
  setBusy(busy) {
    this.busy = busy;
    if (this.hasNextTarget) this.nextTarget.disabled = busy;
    if (this.hasBackTarget) this.backTarget.disabled = busy;
  }

  // Enter has to be wired by hand. Now that the footer owns the only primary
  // action, the contact step's form has five text fields and no submit button —
  // and the browser only submits a buttonless form implicitly when exactly one
  // field blocks implicit submission, so it does nothing here. The code step
  // isn't even a form. Both route through next() so Enter and the footer button
  // stay one dispatch path.
  submitOnEnter(event) {
    // Cancelling the keydown also suppresses any implicit submission a browser
    // might still attempt, so the form's submit action can't fire alongside us.
    event.preventDefault();
    // Both posting steps disable the footer button while in flight; Enter
    // auto-repeats, so without this a held key queues duplicate requests.
    if (this.hasNextTarget && this.nextTarget.disabled) return;
    this.next();
  }

  back() {
    this.clearError();
    if (this.index > 0) {
      this.index -= 1;
      // Never step "back" into the verify screen — return to the contact form.
      if (this.stepTargets[this.index].hasAttribute("data-verify-step")) this.index -= 1;
      this.showStep();
    }
  }

  choose(event) {
    const button = event.currentTarget;
    const group = button.dataset.group;
    this.answers[group] = button.dataset.value;
    button.closest("[data-choice-group]").querySelectorAll("button[data-group]").forEach((b) => {
      b.classList.toggle("is-active", b === button);
    });
    this.clearError();
  }

  showStep() {
    this.stepTargets.forEach((el, i) => { el.hidden = i !== this.index; });
    const current = this.stepTargets[this.index];
    if (this.hasBackTarget) this.backTarget.hidden = this.index === 0;
    if (this.hasNextTarget) {
      this.nextTarget.hidden = false;
      this.nextTarget.disabled = false;
      this.nextTarget.textContent = this.nextLabel(current);
    }
    this.updateProgress();
  }

  // "Next" through the questions, then a label that says what the step actually
  // does — the design's "Finish" on the last one.
  nextLabel(step) {
    if (step.hasAttribute("data-verify-step")) return "Verify & submit";
    if (step.querySelector("form")) return "Finish";
    return "Next";
  }

  // The steps a visitor is counted through: everything after the intro EXCEPT
  // the verification screen, which is a detour off the contact step rather than
  // another question. Derived from the DOM so adding/removing a question in the
  // partial doesn't need a matching edit here.
  get countedSteps() {
    return this.stepTargets.filter((el, i) => i > 0 && !el.hasAttribute("data-verify-step"));
  }

  updateProgress() {
    const counted = this.countedSteps;
    const total = counted.length;
    const current = this.stepTargets[this.index];
    // The verify screen sits past the last counted step — hold the bar full
    // there rather than dropping it back to the contact step's position.
    const position = current && current.hasAttribute("data-verify-step")
      ? total
      : counted.indexOf(current) + 1;

    let label = "";
    if (position > 0) {
      const remaining = total - position;
      label = `Step ${position} of ${total}`;
      if (remaining > 0) label += ` · ${remaining} to go`;
    }
    this.setProgress(label, total > 0 ? Math.round((position / total) * 100) : 0);
  }

  setProgress(label, percent) {
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = label;
    if (this.hasProgressFillTarget) this.progressFillTarget.style.width = `${percent}%`;
    if (this.hasProgressTrackTarget) this.progressTrackTarget.setAttribute("aria-valuenow", String(percent));
  }

  // Contact form submit → validate, then request a verification code and
  // advance to the code step. Any send failure degrades to a plain submit.
  async requestCode(event) {
    // Called from the footer's "Finish" and from submitOnEnter, neither of
    // which passes an event to cancel. The form's own submit action is a
    // safety net only — with no submit button the browser never fires it.
    event?.preventDefault();
    if (this.busy) return;
    this.clearError();
    const email = this.emailTarget.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError("Please enter a valid email address.");
      this.emailTarget.focus();
      return;
    }
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : "";
    if (!phone) {
      this.showError("Please enter your mobile phone number.");
      this.phoneTarget.focus();
      return;
    }
    const zip = this.formTarget.querySelector("input[name=zip]");
    if (zip && zip.value.trim() && !/^\d{5}(-\d{4})?$/.test(zip.value.trim())) {
      this.showError("Please enter a valid zip code.");
      zip.focus();
      return;
    }

    // Hold the footer down for the whole in-flight window. Enter auto-repeats
    // while held, and this step POSTs — without this, a held key sends a burst
    // of /_register calls (so a burst of verification emails), and if
    // /_register is failing, a burst of /_lead calls behind it: duplicate notes
    // and duplicate notifications for the agent. Disabling covers both entry
    // points at once, since submitOnEnter checks the same flag a click does.
    this.setBusy(true);
    try {
      const sent = await this.postRegister();
      if (!sent) {
        await this.sendLead(); // code couldn't be sent — don't lose the lead
        return;
      }
      if (this.hasCodeHintTarget) {
        this.codeHintTarget.textContent = `We sent a 6-digit code to ${this.identifier()}.`;
      }
      this.index = this.stepTargets.findIndex((el) => el.hasAttribute("data-verify-step"));
      this.showStep();
      if (this.hasCodeTarget) this.codeTarget.focus();
    } finally {
      this.setBusy(false);
    }
  }

  async verifyAndSubmit() {
    if (this.busy) return;
    this.clearError();
    const code = (this.hasCodeTarget ? this.codeTarget.value : "").replace(/\D/g, "");
    if (code.length !== 6) {
      this.showError("Enter the 6-digit code from your message.");
      return;
    }
    // Stays frozen THROUGH sendLead, not just through the code check. Releasing
    // it after verification but before the lead lands left a window where a
    // second press re-verified and filed the lead again — duplicate notes and
    // notifications for the agent, on the slow connections most likely to
    // provoke a second press.
    this.setBusy(true);

    const body = new FormData();
    body.append("identifier", this.identifier());
    body.append("code", code);
    let ok = false;
    let data = {};
    try {
      try {
        const response = await fetch("/auth/connect/code", {
          method: "POST",
          headers: { Accept: "application/json" },
          body,
          credentials: "same-origin",
        });
        ok = response.ok;
        data = await response.json().catch(() => ({}));
      } catch (_e) {
        ok = false;
      }

      if (!ok) {
        this.showError("That code is invalid or has expired. Try again or resend.");
        return;
      }
      this.verified = true;
      // Verification signed the visitor in, and this walkthrough never leaves
      // the page — tell the header (and anything else session-dependent) so it
      // stops offering "Sign in".
      announceSessionChange({ source: "preapproval" });
      await this.sendLead();
    } finally {
      this.setBusy(false);
    }
  }

  async resendCode(event) {
    event.preventDefault();
    if (this.busy) return;
    this.clearError();
    this.setBusy(true);
    try {
      const sent = await this.postRegister();
      if (sent && this.hasCodeHintTarget) {
        this.codeHintTarget.textContent = `We sent a new code to ${this.identifier()}.`;
      } else if (!sent) {
        this.showError("We couldn't resend a code right now. You can submit without verifying.");
      }
    } finally {
      this.setBusy(false);
    }
  }

  // The one path that files a lead with no request ahead of it, so it guards
  // itself as well as deferring to an in-flight verify — without the busy
  // check, clicking this while /_lead was already in flight filed it twice.
  async skipVerification(event) {
    event.preventDefault();
    if (this.busy) return;
    this.clearError();
    this.setBusy(true);
    try {
      await this.sendLead();
    } finally {
      this.setBusy(false);
    }
  }

  // POST /_register — captures both required identifiers and sends the sign-in
  // code by email. Returns true when the request was accepted.
  async postRegister() {
    this.verificationStage = "email";
    const body = new FormData();
    body.append("email", this.emailTarget.value.trim());
    body.append("phone", this.phoneTarget.value.trim());
    const name = this.formTarget.querySelector("input[name=name]");
    if (name && name.value.trim()) body.append("name", name.value.trim());
    const pageType = this.formTarget.querySelector("input[name=page_type]");
    if (pageType) body.append("page_type", pageType.value);
    const returnPath = this.formTarget.querySelector("input[name=return_path]");
    if (returnPath) body.append("return_to", returnPath.value);
    body.append("ss_website_url", "");
    try {
      const response = await fetch("/_register", {
        method: "POST",
        headers: { Accept: "application/json" },
        body,
        credentials: "same-origin",
      });
      return response.ok;
    } catch (_e) {
      return false;
    }
  }

  // Initial registration is verified through email. Phone is collected as CRM
  // contact data but is not used as an authentication identifier.
  identifier() {
    return this.emailTarget.value.trim();
  }

  // The single /_lead post carrying the whole questionnaire.
  async sendLead() {
    const lines = ["Mortgage pre-approval request."];
    Object.entries(ANSWER_LABELS).forEach(([key, label]) => {
      if (this.answers[key]) lines.push(`${label}: ${this.answers[key]}.`);
    });
    const zip = this.formTarget.querySelector("input[name=zip]");
    if (zip && zip.value.trim()) lines.push(`Zip code: ${zip.value.trim()}.`);
    lines.push(this.verified ? "Email verified via sign-in code." : "Contact not yet verified.");
    this.formTarget.querySelector("input[name=message]").value = lines.join(" ");

    let response;
    try {
      response = await fetch("/_lead", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(this.formTarget),
        credentials: "same-origin",
      });
    } catch (_e) {
      this.formTarget.submit(); // transport failure → native no-JS path
      return;
    }

    if (response.ok) {
      this.stepTargets.forEach((el) => { el.hidden = true; });
      if (this.hasBackTarget) this.backTarget.hidden = true;
      if (this.hasNextTarget) this.nextTarget.hidden = true;
      this.successTarget.hidden = false;
      this.setProgress("All done", 100);
      return;
    }
    const data = await response.json().catch(() => ({}));
    this.showError(data.error || "We couldn't send your request. Please try again.");
  }

  showError(text) {
    this.errorTarget.textContent = text;
    this.errorTarget.classList.add("is-visible");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.remove("is-visible");
  }
}
